import { useEffect, useRef, useState } from 'react';

import AccordionV2 from '@zola/zola-ui/src/components/AccordionV2';
import { AccordionV2Type } from '@zola/zola-ui/src/components/AccordionV2/AccordionV2';
import {
  AccordionSection,
  AccordionSectionHeading,
} from '@zola/zola-ui/src/components/AccordionV2/AccordionV2.styles';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import {
  SideDrawerFooterContainer,
  SideDrawerHeaderContainer,
  SideDrawerContentContainer,
} from '@zola/zola-ui/src/components/SideDrawer';
import { CloseIcon } from '@zola/zola-ui/src/components/SvgIcons';
import { FONT } from '@zola/zola-ui/src/styles/emotion';

import styled from '@emotion/styled';
import cx from 'classnames';
import _isEqual from 'lodash/isEqual';

import { vendorSearchSortingToString } from '~/pages/couples/explore/components/SearchResults/components/ManualSortingOptions/ManualSortingOptions';
import { useSearchResultsPageContext } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageContext';
import { getSortingLabel } from '~/pages/couples/explore/components/SearchResults/utils/sortHelpers';
import { AllFacetParentKey, OptionFacetCategoryType } from '~/types/types';

import { isSortingFilter } from '../FacetButton/FacetButton';
import { FacetDrawerItem } from './FacetDrawerItem';
import { StyledFacetSideDrawer } from './FacetSideDrawer.styles';

import styles from './FacetSideDrawer.module.less';

export type FacetSideDrawerProps = {
  /** the flag that toggles whether the drawer is visible */
  drawerOpenTo: null | true | AllFacetParentKey;
  /** the function that sets displayDrawer to false */
  onClose: () => void;
  categories: OptionFacetCategoryType[];
  clearFilters: () => void;
  className?: string;
};

/** The accordion animation interferes with scrolling to the correct section, disable it before opening the sidebar, then re-enable */
function useSidebarAnimation(
  itemButtonRefs: AccordionV2Type['itemButtonRefs'],
  drawerOpenTo: string | boolean | null
) {
  const [disableAnimation, setDisableAnimation] = useState(true);
  const [drawerOpenToState, setDrawerOpenToState] = useState([]);

  useEffect(() => {
    const itemFromSelectedCategory = itemButtonRefs?.current[`id-${drawerOpenTo}`];
    // Disable the accordion animation before opening the side bar
    setDisableAnimation(true);
    requestAnimationFrame(() => {
      // Open the side drawer to the correct section
      setDrawerOpenToState(drawerOpenToState);
      requestAnimationFrame(() => {
        // Scroll to the correct section
        itemFromSelectedCategory?.scrollIntoView({
          behavior: 'smooth',
        });
        // Re-add the accordion animation
        setDisableAnimation(false);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpenTo]);

  return { disableAnimation };
}

export const StyledAccordion = styled(AccordionV2)<{ noBorder: boolean; noPadding: boolean }>`
  flex: auto;

  &&& {
    ${AccordionSection} {
      ${({ noPadding }) => (noPadding ? 'padding: 0;' : '')}
    }

    ${AccordionSection}:first-of-type {
      ${({ noBorder }) => (noBorder ? 'border-top: none;' : '')}
    }
    ${AccordionSection}:last-of-type {
      ${({ noBorder }) => (noBorder ? 'border-bottom: none;' : '')}
    }
  }

  ${AccordionSectionHeading} {
    font-weight: ${FONT.FONT_WEIGHT_BOLD};
  }
`;

export const SearchFacetAccordion = ({
  className,
  categories,
  itemButtonRefs,
  initialExpandedItems,
  noBorder = false,
  noSort = false,
  noPadding = false,
  hasSeparateSubmitControl,
  collapseOnPriceChange,
}: {
  className?: string;
  categories: OptionFacetCategoryType[];
  itemButtonRefs?: AccordionV2Type['itemButtonRefs'];
  initialExpandedItems?: AccordionV2Type['initialExpandedItems'];
  noBorder?: boolean;
  noSort?: boolean;
  noPadding?: boolean;
  hasSeparateSubmitControl: boolean;
  collapseOnPriceChange: boolean;
}) => {
  const { selectedFacetOptions, vendorSearch } = useSearchResultsPageContext();

  const [collapseAll, setCollapseAll] = useState(new Date().valueOf().toString());

  return (
    <StyledAccordion
      noBorder={noBorder}
      noPadding={noPadding}
      className={className}
      accordionHeaderComponent={'div'}
      collapseAll={collapseAll}
      items={categories
        .filter((category) => !isSortingFilter(category) || !noSort)
        // Filter out sections that shouldn't be displayed
        .filter(
          (category) =>
            !category.dependsOnKey ||
            selectedFacetOptions?.some(
              (selectedFacetOption) => selectedFacetOption.key === category.dependsOnKey
            )
        )
        .map((category) => {
          return {
            id: `id-${category.key}`,
            hed: isSortingFilter(category) ? (
              <div className={styles.sortingHeader}>
                <div>Sort by</div>
                <div className={styles.sortingSubHeading}>
                  {getSortingLabel(
                    vendorSearch.searchName,
                    vendorSearchSortingToString(vendorSearch)
                  )}
                </div>
              </div>
            ) : (
              category.displayName
            ),
            dek: (
              <FacetDrawerItem
                category={category}
                hasSeparateSubmitControl={hasSeparateSubmitControl}
                collapseAll={
                  collapseOnPriceChange
                    ? () => setCollapseAll(new Date().valueOf().toString())
                    : undefined
                }
              />
            ),
          };
        })}
      allowMultiple
      title={null}
      itemButtonRefs={itemButtonRefs}
      arrowOnLeft
      initialExpandedItems={initialExpandedItems}
    />
  );
};

export const FacetSideDrawer = ({
  onClose,
  categories = [],
  drawerOpenTo,
  clearFilters,
  className,
}: FacetSideDrawerProps) => {
  const itemButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const { selectedFacetOptions, vendorSearch } = useSearchResultsPageContext();

  const { disableAnimation } = useSidebarAnimation(itemButtonRefs, drawerOpenTo);

  useEffect(() => {
    (document.body.parentElement as HTMLElement).style.overflow = drawerOpenTo ? 'hidden' : '';

    return () => {
      (document.body.parentElement as HTMLElement).style.overflow = '';
    };
  }, [drawerOpenTo]);

  return (
    <div className={cx(styles.filterSideDrawer, className)}>
      <StyledFacetSideDrawer
        displayDrawer={drawerOpenTo !== null || drawerOpenTo === true}
        onClose={onClose}
      >
        {{
          header: ({ handleClose }) => (
            <SideDrawerHeaderContainer>
              <CloseIcon onClick={handleClose} height={20} width={20} />
            </SideDrawerHeaderContainer>
          ),
          content: (
            <SideDrawerContentContainer className={styles.contentContainer}>
              <SearchFacetAccordion
                className={cx({ [styles.disableAnimation]: disableAnimation })}
                categories={categories}
                itemButtonRefs={itemButtonRefs}
                initialExpandedItems={drawerOpenTo !== null ? `id-${drawerOpenTo}` : []}
                hasSeparateSubmitControl={true}
                collapseOnPriceChange={false}
                noBorder
              />
            </SideDrawerContentContainer>
          ),
          footer: () => (
            <SideDrawerFooterContainer className={styles.buttonsContainer}>
              <LinkV2
                className={styles.clearButton}
                type="button"
                role="button"
                variant="primary"
                onClick={() => {
                  clearFilters();
                }}
                disabled={
                  _isEqual(selectedFacetOptions, []) &&
                  (!vendorSearch.price || _isEqual(vendorSearch.price, { min: null, max: null }))
                }
              >
                Clear all
              </LinkV2>
              <ButtonV3
                className={styles.applyButton}
                onClick={() => {
                  onClose();
                }}
              >
                Done
              </ButtonV3>
            </SideDrawerFooterContainer>
          ),
        }}
      </StyledFacetSideDrawer>
    </div>
  );
};

export default FacetSideDrawer;
